import { create } from 'zustand';
import { acknowledgeFeedback, editFeedbackDraft, emptyFeedbackDocument, enqueueFeedback } from '@cerebro-diem/core';
import type { FeedbackKind, FeedbackRow, FeedbackStorage, PendingFeedback } from '@cerebro-diem/core';
import { feedbackStorage } from '../services/feedbackStorage';

interface FeedbackDialogState {
  ownerId: string | null;
  screen: string | null;
  kind: FeedbackKind;
  message: string;
  pending: PendingFeedback[];
  storageError: string | null;
  sendError: string | null;
  ready: boolean;
  sending: boolean;
  open: (screen: string) => void;
  close: () => void;
  setKind: (kind: FeedbackKind) => void;
  setMessage: (message: string) => void;
  bindOwner: (ownerId: string | null) => Promise<void>;
  flush: () => Promise<void>;
  retrySavingDraft: () => Promise<void>;
  enqueue: (row: FeedbackRow) => Promise<string>;
  retry: () => Promise<void>;
}
export async function withFeedbackLock<T>(id: string, run: () => Promise<T>): Promise<T> {
  if (!globalThis.navigator?.locks?.request) throw new Error('This browser cannot safely send feedback across windows (Web Locks unavailable). Your waiting reports are kept on this device');
  return navigator.locks.request(`cerebro-feedback-send:${id}`, run);
}

/** Visible input can survive a rejected write. Only committed documents can be sent. */
export function createFeedbackStore(storage: FeedbackStorage, send: (entry: PendingFeedback) => Promise<string>, lock = withFeedbackLock) {
  return create<FeedbackDialogState>((set, get) => {
    let generation = 0;
    const freshContext = () => ({ revision: 0, loaded: false, inputVersion: 0, staged: false, input: { kind: 'bug' as FeedbackKind, message: '' }, error: null as string | null, reading: Promise.resolve() });
    const authorContexts = new Map<string, ReturnType<typeof freshContext>>();
    let context = freshContext();
    let writes: Promise<void> = Promise.resolve();
    let reading: Promise<void> = Promise.resolve();
    const current = (owner: string, version: number) => get().ownerId === owner && generation === version;
    const edit = (input: { kind: FeedbackKind; message: string }) => {
      set(input);
      const captured = context;
      const inputVersion = ++captured.inputVersion;
      captured.input = input;
      captured.staged = true;
      const owner = get().ownerId;
      const version = generation;
      const opening = captured.reading;
      if (!owner) { set({ storageError: 'Sign in before saving feedback; your input is still here' }); return; }
      writes = writes.then(async () => {
        await opening;
        if (captured.error) return;
        try {
          const doc = await storage.mutate(owner, value => editFeedbackDraft(value, captured.revision, input));
          captured.revision = doc.draft.revision;
          if (captured.inputVersion === inputVersion) captured.staged = false;
          if (current(owner, version)) set({ pending: doc.pending });
        } catch (error) {
          captured.error = error instanceof Error ? error.message : 'Feedback storage failed; your input is still here';
          if (current(owner, version)) set({ storageError: captured.error });
        }
      });
    };
    return {
      ownerId: null, screen: null, kind: 'bug', message: '', pending: [], storageError: null, sendError: null, ready: false, sending: false,
      open: screen => set({ screen }),
      close: () => set({ screen: null }),
      setKind: kind => edit({ kind, message: get().message }),
      setMessage: message => edit({ message, kind: get().kind }),
      bindOwner: ownerId => {
        const version = ++generation;
        const previousWrites = writes;
        const captured = ownerId ? authorContexts.get(ownerId) ?? freshContext() : freshContext();
        if (ownerId) authorContexts.set(ownerId, captured);
        context = captured;
        set({ ownerId, screen: null, ...(captured.staged ? captured.input : emptyFeedbackDocument().draft), pending: [], storageError: captured.error, sendError: null, ready: false, sending: false });
        reading = (async () => {
          if (!ownerId) return;
          try {
            await previousWrites;
            const doc = await storage.read(ownerId);
            if (!captured.staged || !captured.loaded && !captured.error) {
              captured.revision = doc.draft.revision;
              captured.loaded = true;
            }
            if (!current(ownerId, version)) return;
            if (!captured.staged) captured.input = { kind: doc.draft.kind, message: doc.draft.message };
            set({ ...captured.input, pending: doc.pending, ready: true, storageError: captured.error });
          } catch (error) {
            captured.error = error instanceof Error ? error.message : 'Feedback storage could not be read; its data has been kept';
            if (current(ownerId, version)) set({ storageError: captured.error });
          }
        })();
        captured.reading = reading;
        return reading;
      },
      flush: async () => { await reading; await writes; if (get().storageError) throw new Error(get().storageError!); },
      retrySavingDraft: async () => {
        const owner = get().ownerId;
        const version = generation;
        const captured = context;
        if (!owner) throw new Error('Sign in before saving feedback');
        await reading; await writes;
        if (!current(owner, version)) throw new Error('The signed-in account changed; your input has not been sent');
        const input = captured.input;
        const inputVersion = captured.inputVersion;
        const operation = writes.then(async () => {
          try {
            if (!captured.loaded) {
              const original = await storage.read(owner);
              if (original.draft.message || original.draft.kind !== 'bug') throw new Error('A stored draft must be recovered first. Your input is still here; copy it before reloading');
              captured.revision = original.draft.revision;
              captured.loaded = true;
            }
            const doc = await storage.mutate(owner, value => editFeedbackDraft(value, captured.revision, input));
            captured.revision = doc.draft.revision;
            captured.error = null;
            if (captured.inputVersion === inputVersion) captured.staged = false;
            if (current(owner, version)) set({ pending: doc.pending, storageError: null, ready: true });
          } catch (error) {
            captured.error = error instanceof Error ? error.message : 'Feedback storage failed; your input is still here';
            if (current(owner, version)) set({ storageError: captured.error });
            throw error;
          }
        });
        writes = operation.then(() => undefined, () => undefined);
        return operation;
      },
      enqueue: async row => {
        const owner = get().ownerId;
        const version = generation;
        if (!owner) throw new Error('Sign in before sending feedback');
        const captured = context;
        const queuedInputVersion = captured.inputVersion;
        await get().flush();
        if (!current(owner, version)) throw new Error('The signed-in account changed; your report was not sent');
        const entry: PendingFeedback = { id: crypto.randomUUID(), ownerId: owner, createdAt: new Date().toISOString(), row };
        const operation = writes.then(async () => {
          try {
            const doc = await storage.mutate(owner, value => enqueueFeedback(value, captured.revision, entry));
            captured.revision = doc.draft.revision;
            if (captured.inputVersion === queuedInputVersion) {
              captured.input = { kind: doc.draft.kind, message: doc.draft.message };
              captured.staged = false;
              captured.error = null;
            }
            if (current(owner, version)) {
              set({ ...(captured.inputVersion === queuedInputVersion ? { kind: doc.draft.kind, message: doc.draft.message } : {}), pending: doc.pending, storageError: captured.error });
            }
            return entry.id;
          } catch (error) {
            captured.error = error instanceof Error ? error.message : 'Feedback could not be queued; your input is still here';
            if (current(owner, version)) set({ storageError: captured.error });
            throw error;
          }
        });
        // The caller receives the rejection and the visible error remains; release the edit chain.
        writes = operation.then(() => undefined, () => undefined);
        return operation;
      },
      retry: async () => {
        const owner = get().ownerId;
        const version = generation;
        if (!owner || get().sending) return;
        set({ sending: true, sendError: null });
        try {
          await reading;
          const snapshot = await storage.read(owner);
          if (!current(owner, version)) return;
          set({ pending: snapshot.pending });
          for (const entry of snapshot.pending) {
            if (!current(owner, version)) return;
            await lock(entry.id, async () => {
              if (!current(owner, version)) return;
              const latest = await storage.read(owner);
              if (!latest.pending.some(item => item.id === entry.id)) return;
              const receipt = await send(entry);
              if (receipt !== entry.id) throw new Error('The server receipt did not match; your waiting report is kept');
              const doc = await storage.mutate(owner, value => acknowledgeFeedback(value, receipt));
              if (current(owner, version)) set({ pending: doc.pending });
            });
          }
        } catch (error) {
          if (current(owner, version)) set({ sendError: error instanceof Error ? error.message : 'Feedback could not be sent; waiting reports are kept' });
        } finally { if (current(owner, version)) set({ sending: false }); }
      },
    };
  });
}
export const useFeedbackDialog = createFeedbackStore(feedbackStorage, async entry => {
  const { sendFeedback } = await import('../services/feedback');
  return sendFeedback(entry);
});
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { void useFeedbackDialog.getState().retry(); });
  window.addEventListener('focus', () => { void useFeedbackDialog.getState().retry(); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void useFeedbackDialog.getState().retry(); });
}
